import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Space, Typography } from "antd";
import { KeyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../shared/hooks";
import { resetUserPassword, RestaurantUser } from "./api";

const { Text } = Typography;

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  user: RestaurantUser | null;
  onSuccess: () => void;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  onClose,
  restaurantId,
  user,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    if (!user) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      resetUserPassword(
        restaurantId,
        user.user_id,
        { new_password: values.new_password },
        () => {
          setLoading(false);
          showSuccess(t("password_reset_successfully"));
          onSuccess();
        },
        (err) => {
          setLoading(false);
          notifyError(t("failed_to_reset_password"), err);
        }
      );
    } catch {
      // Form validation failed
    }
  };

  return (
    <Modal
      title={
        <Space>
          <KeyOutlined />
          {t("reset_password")}
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t("reset")}
      cancelText={t("cancel")}
      confirmLoading={loading}
      destroyOnHidden
    >
      {user && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{t("resetting_password_for")}</Text>
          <Text strong style={{ marginLeft: 8 }}>
            {user.email}
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="new_password"
          label={t("new_password")}
          rules={[
            { required: true, message: t("please_enter_new_password") },
            { min: 6, message: t("password_min_length") },
          ]}
        >
          <Input.Password placeholder={t("enter_new_password")} />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label={t("confirm_password")}
          dependencies={["new_password"]}
          rules={[
            { required: true, message: t("please_confirm_password") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t("passwords_do_not_match")));
              },
            }),
          ]}
        >
          <Input.Password placeholder={t("confirm_new_password")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
